/**
 * Loyalty Rive Loader
 * ───────────────────────────────────────────────────────────────
 * Lazily initialises Rive animations on <canvas data-rive-src> elements.
 *
 * - Loads the Rive WASM runtime only when the first canvas enters the viewport.
 * - Uses IntersectionObserver to play/pause animations based on visibility.
 * - Automatically sizes the canvas to its CSS container.
 *
 * Include this script once in the layout or on the rewards page:
 *   <script src="{{ 'loyalty-rive-loader.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  var RIVE_CDN = 'https://unpkg.com/@rive-app/canvas@2.21.6';
  var riveModule = null;
  var riveLoading = null;
  var instances = [];

  /**
   * Load the Rive runtime from CDN (once).
   */
  function loadRiveRuntime() {
    if (riveModule) return Promise.resolve(riveModule);
    if (riveLoading) return riveLoading;

    riveLoading = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = RIVE_CDN;
      script.onload = function () {
        if (window.rive) {
          riveModule = window.rive;
          resolve(riveModule);
        } else {
          reject(new Error('Rive runtime not found on window.rive'));
        }
      };
      script.onerror = function () {
        reject(new Error('Failed to load Rive runtime from CDN'));
      };
      document.head.appendChild(script);
    });

    return riveLoading;
  }

  /**
   * Initialise a single Rive canvas.
   */
  function initCanvas(canvas) {
    var src = canvas.dataset.riveSrc;
    if (!src || canvas.dataset.riveReady) return;
    canvas.dataset.riveReady = 'loading';

    loadRiveRuntime().then(function (rive) {
      /* Size canvas to container */
      var rect = canvas.getBoundingClientRect();
      var dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      var config = {
        src: src,
        canvas: canvas,
        autoplay: true,
        automaticallyHandleEvents: true,
      };

      /* Optional state machine name */
      var stateName = canvas.dataset.riveState;
      if (stateName) {
        config.stateMachines = stateName;
      }

      var riveInstance = new rive.Rive(config);

      canvas.dataset.riveReady = 'true';

      instances.push({
        canvas: canvas,
        instance: riveInstance,
      });
    }).catch(function (err) {
      console.warn('[loyalty-rive] Failed to init:', src, err);
      canvas.dataset.riveReady = 'error';
    });
  }

  /**
   * Observe canvases for viewport entry (lazy load + play/pause).
   */
  function observeCanvases() {
    var canvases = document.querySelectorAll('canvas[data-rive-src]:not([data-rive-ready])');
    if (canvases.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      /* Fallback: init all immediately */
      canvases.forEach(initCanvas);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var canvas = entry.target;
        if (entry.isIntersecting) {
          if (!canvas.dataset.riveReady) {
            initCanvas(canvas);
          } else {
            /* Resume playback */
            var match = instances.find(function (i) { return i.canvas === canvas; });
            if (match && match.instance.play) match.instance.play();
          }
        } else {
          /* Pause when off-screen to save resources */
          var match = instances.find(function (i) { return i.canvas === canvas; });
          if (match && match.instance.pause) match.instance.pause();
        }
      });
    }, { rootMargin: '200px' });

    canvases.forEach(function (c) { observer.observe(c); });
  }

  /**
   * Handle window resize — update canvas dimensions.
   */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      instances.forEach(function (item) {
        var rect = item.canvas.getBoundingClientRect();
        var dpr = window.devicePixelRatio || 1;
        item.canvas.width = rect.width * dpr;
        item.canvas.height = rect.height * dpr;
        if (item.instance.resizeDrawingSurfaceToCanvas) {
          item.instance.resizeDrawingSurfaceToCanvas();
        }
      });
    }, 150);
  });

  /* Init on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCanvases);
  } else {
    observeCanvases();
  }

  /* Re-scan after Yotpo widgets render (they may inject new canvases) */
  document.addEventListener('yotpo-loyalty:ready', function () {
    setTimeout(observeCanvases, 500);
  });

  /* Also watch for dynamically added canvases */
  if ('MutationObserver' in window) {
    var domObserver = new MutationObserver(function (mutations) {
      var hasNewCanvas = mutations.some(function (m) {
        return Array.from(m.addedNodes).some(function (n) {
          return n.nodeType === 1 && (
            (n.tagName === 'CANVAS' && n.dataset.riveSrc) ||
            n.querySelector && n.querySelector('canvas[data-rive-src]')
          );
        });
      });
      if (hasNewCanvas) observeCanvases();
    });
    domObserver.observe(document.body, { childList: true, subtree: true });
  }
})();
